// CTSK-245 — Type-ahead user search widget.
//
// Reusable input that debounces keystrokes into GET /api/v1/users?q=&limit=&fields=lite
// and renders a dropdown of matches. Selection fires `onSelect(user)` — the
// parent form decides what to do with the pick (populate an email input, add
// directly to a list, etc.).
//
// Used in OrganizationsPanel's MembersPanel add-member form (replacing the
// raw email input) but designed to be reusable for any future user-picker
// surface (group-add, role assignment, task assignee picker, etc.).
//
// Design notes:
//   * Debounce ~200ms so a burst of keystrokes fires one request, not N.
//   * Lite payload (`fields=lite`) skips the per-user group/role bulk-load
//     server-side — critical for per-keystroke perf.
//   * Case-insensitive substring match on displayName + email (backend uses
//     ilike). AC7 cross-org isolation is deferred to a follow-up ticket;
//     the existing `users:view` cap-gate covers the emissary/steward/envoy
//     layer that this admin surface targets.
//   * Keyboard nav: ArrowUp/ArrowDown to move highlight, Enter to select,
//     Escape to close. ARIA combobox pattern with listbox popup + roving
//     option ids.
//   * Empty-state renders a helpful hint pointing at the (future) invite
//     flow — placeholder link since CTSK-238-adjacent invite hasn't shipped.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiList } from '../lib/api.js';

/** Minimal user shape the type-ahead renders. Matches `GET /users?fields=lite`
 *  envelope (which stubs `groupIds` / `roleIds` to [] but keeps the id/name/
 *  email/avatar fields the picker actually uses). */
export interface UserLite {
	id: string;
	displayName: string;
	email: string;
	avatarUrl?: string | null;
	isActive?: boolean;
	// Kept optional so consumers using the /users full shape can pass through.
	groupIds?: string[];
	roleIds?: string[];
}

interface UserTypeaheadProps {
	/** Current text in the input (controlled). Parent manages the string so
	 *  it can be cleared/prefilled after a selection lands. */
	value: string;
	onChange: (v: string) => void;
	/** Fired when the user picks a row. Parent handles the actual "add to
	 *  org / group / assignee list" behavior — this widget just surfaces the
	 *  pick. */
	onSelect: (user: UserLite) => void;
	/** Optional placeholder for the input. Default: "Search users by name or email…". */
	placeholder?: string;
	/** Optional aria-label for the input. Default: "Search users". */
	ariaLabel?: string;
	/** Optional debounce override in ms. Default 200 per CTSK-245 AC3. */
	debounceMs?: number;
	/** Optional max rows to show. Default 8 (server caps at 200 anyway). */
	limit?: number;
	/** Optional autofocus on mount. */
	autoFocus?: boolean;
	/** Optional CSS style override for the input. */
	inputStyle?: React.CSSProperties;
	/** Optional disable — used when a submit is in flight so the picker
	 *  can't re-fire during the round-trip. */
	disabled?: boolean;
}

const LISTBOX_ID = 'user-typeahead-listbox';
const OPTION_ID_PREFIX = 'user-typeahead-option-';

export function UserTypeahead({
	value,
	onChange,
	onSelect,
	placeholder = 'Search users by name or email…',
	ariaLabel = 'Search users',
	debounceMs = 200,
	limit = 8,
	autoFocus = false,
	inputStyle,
	disabled = false,
}: UserTypeaheadProps) {
	// Debounced query string — separate from the input value so keystrokes
	// don't fire /users per-char.
	const [debouncedQ, setDebouncedQ] = useState(value);
	// Whether the dropdown is open. Independent of value length so a re-focus
	// on a valid query can re-open without a keystroke.
	const [open, setOpen] = useState(false);
	// Keyboard-nav highlight index into the results array. -1 = nothing
	// highlighted (mouse-only mode); 0..N-1 = highlighted row for Enter select.
	const [highlight, setHighlight] = useState<number>(-1);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Debounce input → debouncedQ.
	useEffect(() => {
		if (value.length === 0) {
			// Empty string bypasses debounce so clearing the input feels snappy.
			setDebouncedQ('');
			return;
		}
		const id = window.setTimeout(() => setDebouncedQ(value), debounceMs);
		return () => window.clearTimeout(id);
	}, [value, debounceMs]);

	// The query. Only fires when there's actual text to search on — an
	// empty q would page the entire user table server-side (limit 200 caps
	// it but still wasteful).
	const searchQ = useQuery({
		queryKey: ['users-typeahead', debouncedQ, limit],
		queryFn: () =>
			apiList<UserLite>('/users', { query: { q: debouncedQ, limit: String(limit), fields: 'lite' } }),
		enabled: debouncedQ.length > 0,
		// Keep previous data during the next fetch to prevent dropdown flicker
		// on subsequent keystrokes. staleTime of a few seconds so a re-open
		// with the same query doesn't burn a network round-trip.
		staleTime: 5_000,
	});

	const results = useMemo(() => searchQ.data?.data ?? [], [searchQ.data]);

	// Reset highlight when results change (e.g. new query landed).
	useEffect(() => {
		setHighlight(results.length > 0 ? 0 : -1);
	}, [results]);

	// Close dropdown when clicking outside. Standard picker pattern.
	useEffect(() => {
		if (!open) return;
		function onDocClick(e: MouseEvent) {
			if (!containerRef.current) return;
			if (!containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', onDocClick);
		return () => document.removeEventListener('mousedown', onDocClick);
	}, [open]);

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
			// Re-open dropdown on arrow-nav from a closed state.
			setOpen(true);
			return;
		}
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setHighlight((h) => (results.length === 0 ? -1 : (h + 1) % results.length));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setHighlight((h) => (results.length === 0 ? -1 : (h - 1 + results.length) % results.length));
				break;
			case 'Enter':
				if (open && highlight >= 0 && results[highlight]) {
					e.preventDefault();
					handleSelect(results[highlight]);
				}
				break;
			case 'Escape':
				setOpen(false);
				setHighlight(-1);
				break;
		}
	}

	function handleSelect(user: UserLite) {
		onSelect(user);
		setOpen(false);
		setHighlight(-1);
		// Return focus to the input in case parent didn't unmount the picker.
		// Preserves keyboard-nav-only workflows.
		inputRef.current?.focus();
	}

	const showDropdown = open && debouncedQ.length > 0;

	return (
		<div
			ref={containerRef}
			style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}
		>
			<input
				ref={inputRef}
				type="text"
				value={value}
				onChange={(e) => {
					onChange(e.target.value);
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				onKeyDown={handleKeyDown}
				placeholder={placeholder}
				disabled={disabled}
				autoFocus={autoFocus}
				role="combobox"
				aria-expanded={showDropdown}
				aria-controls={LISTBOX_ID}
				aria-autocomplete="list"
				aria-label={ariaLabel}
				aria-activedescendant={
					showDropdown && highlight >= 0 ? `${OPTION_ID_PREFIX}${highlight}` : undefined
				}
				style={{
					width: '100%',
					padding: '6px 10px',
					fontSize: 13,
					border: '1px solid var(--border-strong)',
					borderRadius: 4,
					...(inputStyle ?? {}),
				}}
			/>
			{showDropdown && (
				<ul
					id={LISTBOX_ID}
					role="listbox"
					aria-label="User search results"
					style={{
						position: 'absolute',
						top: '100%',
						left: 0,
						right: 0,
						marginTop: 2,
						padding: 0,
						listStyle: 'none',
						maxHeight: 280,
						overflowY: 'auto',
						background: 'var(--surface-raised)',
						border: '1px solid var(--border-strong)',
						borderRadius: 4,
						boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
						zIndex: 100,
					}}
				>
					{searchQ.isLoading && results.length === 0 && (
						<li style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-muted)' }}>Searching…</li>
					)}
					{searchQ.isError && (
						<li style={{ padding: '8px 10px', fontSize: 12, color: 'var(--danger-primary, #B3261E)' }}>
							Search failed. Check your connection.
						</li>
					)}
					{!searchQ.isLoading && !searchQ.isError && results.length === 0 && (
						<li style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-muted)' }}>
							No users match <em>&ldquo;{debouncedQ}&rdquo;</em>.
							{' '}
							{/* CTSK-245 AC6 — pointer to invite flow. Kept as inline
							    text since the invite flow (CTSK-238-adjacent) hasn't
							    shipped yet. When it does, wire this to a real link. */}
							<span style={{ display: 'block', marginTop: 4, fontSize: 11 }}>
								Need to bring someone new in? Ask an org owner to create the user first
								(<code>Settings → Users</code>) — invite flow lands in the CTSK-244 epic.
							</span>
						</li>
					)}
					{results.map((u, i) => (
						<li
							key={u.id}
							id={`${OPTION_ID_PREFIX}${i}`}
							role="option"
							aria-selected={highlight === i}
							onMouseEnter={() => setHighlight(i)}
							onMouseDown={(e) => {
								// mousedown (not click) so the input's blur doesn't
								// close the dropdown before selection fires.
								e.preventDefault();
								handleSelect(u);
							}}
							style={{
								padding: '6px 10px',
								fontSize: 13,
								cursor: 'pointer',
								background: highlight === i ? 'var(--surface-sunken)' : 'transparent',
								borderBottom: '1px solid var(--border-subtle)',
								display: 'flex',
								alignItems: 'baseline',
								gap: 8,
							}}
						>
							<span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
								{u.displayName}
							</span>
							<span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
								{u.email}
							</span>
							{u.isActive === false && (
								<span
									style={{
										fontSize: 10,
										padding: '1px 6px',
										borderRadius: 8,
										background: 'var(--surface-sunken)',
										color: 'var(--text-muted)',
										textTransform: 'uppercase',
										letterSpacing: 0.3,
										marginLeft: 'auto',
									}}
									title="This user account is disabled"
								>
									disabled
								</span>
							)}
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
